import React, { useEffect, useState } from "react";
import { NavLink, Navigate, useParams } from "react-router-dom";
import { base } from "routes/ModerationRoutes";
import { api } from "services/api";
import { BannedPost, ModerationDecision } from "services/api/banInfo/types";
import "./BlockedEntitiesPage.scss";
import { BlockedEntityTab } from "./model/types";
import { useBlockedEntities } from "./model/useBlockedEntities";
import { BlockedDetailsPanel } from "./ui/BlockedDetailsPanel";
import { BlockedFilters } from "./ui/BlockedFilters";
import { BlockedInspectionModal } from "./ui/BlockedInspectionModal";
import { BlockedTable } from "./ui/BlockedTable";

const VALID_TABS: BlockedEntityTab[] = ["users", "groups", "channels"];

type InspectionState = {
  type: "log" | "post";
  id: string;
  data: ModerationDecision | BannedPost | null;
  loading: boolean;
  error: string;
};

const extractApiError = (error: unknown): string => {
  const value = error as any;
  return value?.response?.data?.message || value?.message || "Request failed.";
};

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

const BlockedEntitiesPage: React.FC = () => {
  const params = useParams<{ tab?: string }>();
  if (!params.tab) {
    return <Navigate to={`${base}/blocked/users`} replace />;
  }

  const tab = params.tab as BlockedEntityTab;

  if (!VALID_TABS.includes(tab)) {
    return <Navigate to={`${base}/blocked/users`} replace />;
  }

  return <BlockedEntitiesContent key={tab} tab={tab} />;
};

const BlockedEntitiesContent: React.FC<{ tab: BlockedEntityTab }> = ({
  tab,
}) => {
  const {
    filters,
    setFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
    items,
    total,
    loading,
    error,
    selected,
    setSelectedId,
    details,
    detailsLoading,
    detailsError,
    detailsPageSize,
    logsPage,
    postsPage,
    logsLoading,
    postsLoading,
    changeLogsPage,
    changePostsPage,
    reasonOptions,
    reasonsLoading,
    reasonsError,
    resetFilters,
    refresh,
  } = useBlockedEntities(tab);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [inspection, setInspection] = useState<InspectionState | null>(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaType, setMediaType] = useState("");
  const [mediaLoading, setMediaLoading] = useState(false);

  useEffect(() => {
    setInspection(null);
    setMediaUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return "";
    });
    setMediaType("");
  }, [tab]);

  useEffect(
    () => () => {
      if (mediaUrl) URL.revokeObjectURL(mediaUrl);
    },
    [mediaUrl],
  );

  const handleExport = async (format: "csv" | "xlsx") => {
    setExporting(true);
    setExportError("");
    try {
      const exportParams = {
        search: filters.search.trim() || undefined,
        reason: filters.reason || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        sort_by:
          filters.sortBy === "name"
            ? tab === "users"
              ? ("username" as const)
              : ("title" as const)
            : filters.sortBy === "created_at"
              ? ("created_at" as const)
              : ("banned_at" as const),
        sort_order: filters.sortOrder,
      };
      const blob =
        tab === "users"
          ? await api.banInfo.exportBannedUsers(exportParams, format)
          : tab === "groups"
            ? await api.banInfo.exportBannedGroups(exportParams, format)
            : await api.banInfo.exportBannedChannels(exportParams, format);
      const date = new Date().toISOString().slice(0, 10);
      saveBlob(blob, `blocked-${tab}-${date}.${format}`);
    } catch (requestError) {
      setExportError(extractApiError(requestError));
    } finally {
      setExporting(false);
    }
  };

  const openInspection = async (type: "log" | "post", id: number | string) => {
    setMediaUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return "";
    });
    setMediaType("");
    setInspection({
      type,
      id: String(id),
      data: null,
      loading: true,
      error: "",
    });

    try {
      const data =
        type === "log"
          ? await api.banInfo.getLog(id)
          : await api.banInfo.getPost(id);
      setInspection({ type, id: String(id), data, loading: false, error: "" });
    } catch (requestError) {
      setInspection({
        type,
        id: String(id),
        data: null,
        loading: false,
        error: extractApiError(requestError),
      });
    }
  };

  const loadMedia = async () => {
    if (!inspection || inspection.type !== "post") return;
    setMediaLoading(true);
    try {
      const blob = await api.banInfo.getMedia(inspection.id);
      const url = URL.createObjectURL(blob);
      setMediaUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return url;
      });
      setMediaType(blob.type || "application/octet-stream");
    } catch (requestError) {
      setInspection((current) =>
        current
          ? { ...current, error: extractApiError(requestError) }
          : current,
      );
    } finally {
      setMediaLoading(false);
    }
  };

  const closeInspection = () => {
    setInspection(null);
    setMediaUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return "";
    });
    setMediaType("");
  };

  return (
    <main className="blocked-page">
      <header className="blocked-page__header">
        <div>
          <span className="blocked-page__eyebrow">Auto-Moderation</span>
          <h1>Blocked entities</h1>
          <p>
            Review users, groups and channels blocked automatically after failed
            moderation checks.
          </p>
        </div>
        <div className="blocked-page__summary">
          <span>
            <b>{loading ? "…" : total.toLocaleString()}</b>
            total in current tab
          </span>
          <span>
            <b>{items.length.toLocaleString()}</b>
            visible on page
          </span>
        </div>
      </header>

      <nav className="blocked-tabs" aria-label="Blocked entity type">
        {VALID_TABS.map((item) => (
          <NavLink
            key={item}
            to={`${base}/blocked/${item}`}
            className={({ isActive }) =>
              isActive ? "blocked-tabs__active" : undefined
            }
          >
            <span className={`blocked-tabs__icon blocked-tabs__icon--${item}`}>
              {item === "users" ? (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm7 8a7 7 0 0 0-14 0" />
                </svg>
              ) : item === "groups" ? (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm8-1a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM2 20a6 6 0 0 1 12 0m0 0a5 5 0 0 1 8 0" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m3 11 18-7-7 18-3-8-8-3Zm8 3 4-4" />
                </svg>
              )}
            </span>
            <span>{item[0].toUpperCase() + item.slice(1)}</span>
          </NavLink>
        ))}
      </nav>

      <BlockedFilters
        tab={tab}
        value={filters}
        reasonOptions={reasonOptions}
        reasonsLoading={reasonsLoading}
        loading={loading || exporting}
        onChange={(next) => {
          setFilters(next);
          setPage(1);
        }}
        onReset={resetFilters}
        onRefresh={refresh}
        onExport={handleExport}
      />

      {exportError ? (
        <div className="blocked-page__notice blocked-page__notice--error">
          Export failed: {exportError}
        </div>
      ) : null}

      {reasonsError ? (
        <div className="blocked-page__notice blocked-page__notice--error">
          Reasons: {reasonsError}
        </div>
      ) : null}

      <div className="blocked-page__workspace">
        <BlockedDetailsPanel
          entity={selected}
          details={details}
          loading={detailsLoading}
          error={detailsError}
          pageSize={detailsPageSize}
          logsPage={logsPage}
          postsPage={postsPage}
          logsLoading={logsLoading}
          postsLoading={postsLoading}
          onLogsPageChange={changeLogsPage}
          onPostsPageChange={changePostsPage}
          onInspectLog={(item) => openInspection("log", item.id)}
          onInspectPost={(item) => openInspection("post", item.id)}
        />

        <BlockedTable
          tab={tab}
          items={items}
          total={total}
          page={page}
          pageSize={pageSize}
          selectedId={selected?.id || ""}
          loading={loading}
          error={error}
          onSelect={setSelectedId}
          onPageChange={setPage}
          onPageSizeChange={(nextSize) => {
            setPageSize(nextSize);
            setPage(1);
          }}
          onRetry={refresh}
        />
      </div>

      {inspection ? (
        <BlockedInspectionModal
          type={inspection.type}
          data={inspection.data}
          loading={inspection.loading}
          error={inspection.error}
          mediaUrl={mediaUrl}
          mediaType={mediaType}
          mediaLoading={mediaLoading}
          onLoadMedia={loadMedia}
          onClose={closeInspection}
        />
      ) : null}
    </main>
  );
};

export default BlockedEntitiesPage;
